/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import {Nagbar} from '@app/components/layout/Nagbar';
import {NagbarButton} from '@app/components/layout/NagbarButton';
import {NagbarContent} from '@app/components/layout/NagbarContent';
import StatusPageStore from '@app/stores/StatusPageStore';
import * as LocaleUtils from '@app/utils/LocaleUtils';
import {getFormattedDateTime} from '@fluxer/date_utils/src/DateFormatting';
import {Trans} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useCallback, useMemo} from 'react';

interface MaintenanceScheduleLabels {
	scheduledForLabel: string;
	scheduledUntilLabel: string;
}

function formatMaintenanceTimeLabel(value: string, locale: string): string {
	const parsedDate = new Date(value);
	if (Number.isNaN(parsedDate.getTime())) {
		return value;
	}
	return getFormattedDateTime(parsedDate, locale);
}

export const ScheduledMaintenanceNagbar = observer(({isMobile}: {isMobile: boolean}) => {
	const scheduledMaintenance = StatusPageStore.scheduledMaintenance;
	const locale = LocaleUtils.getCurrentLocale();

	const scheduleLabels = useMemo<MaintenanceScheduleLabels | null>(() => {
		if (!scheduledMaintenance) {
			return null;
		}

		return {
			scheduledForLabel: formatMaintenanceTimeLabel(scheduledMaintenance.scheduledFor, locale),
			scheduledUntilLabel: formatMaintenanceTimeLabel(scheduledMaintenance.scheduledUntil, locale),
		};
	}, [locale, scheduledMaintenance]);

	const handleOpenStatusPage = useCallback(() => {
		if (!scheduledMaintenance) {
			return;
		}

		window.open(scheduledMaintenance.url, '_blank', 'noopener,noreferrer');
	}, [scheduledMaintenance]);

	if (!scheduledMaintenance || !scheduleLabels) {
		return null;
	}

	return (
		<Nagbar isMobile={isMobile} backgroundColor="#b45309" textColor="#ffffff">
			<NagbarContent
				isMobile={isMobile}
				message={
					<Trans>
						<strong>{scheduledMaintenance.name}</strong> is scheduled from{' '}
						<strong>{scheduleLabels.scheduledForLabel}</strong> to <strong>{scheduleLabels.scheduledUntilLabel}</strong>
					</Trans>
				}
				actions={
					<NagbarButton isMobile={isMobile} onClick={handleOpenStatusPage}>
						<Trans>View Status Page</Trans>
					</NagbarButton>
				}
			/>
		</Nagbar>
	);
});
